import { groupBy } from 'lodash';
import React from 'react';

import {
  CostCodeAdjustmentItem,
  IntegrationStatusDto,
  JobChangeOrderDto,
  JobCostCodeSummaryGpDto,
  JobCostDetailDto,
  JobInvoiceDataDto,
  JobPoDataDto,
  JobSummaryDto,
} from 'src/api/FiretrolApiClient';
import { ctcSnapshotOptions } from 'src/api/queries/ctc/ctcSnapshotOptions';
import { useCtcJob } from 'src/api/queries/ctc/useCtcJob';
import { useCtcAdjustments } from 'src/api/queries/ctc/useCtcAdjustments';
import { useCtcCurrentMonthStatus } from 'src/api/queries/ctc/useCtcCurrentMonthStatus';
import {
  useCtcMaterials,
  useCtcMaterialsTotal,
} from 'src/api/queries/ctc/useCtcMaterials';
import { useCtcNavigation } from 'src/api/queries/ctc/useCtcNavigation';
import { useJob } from 'src/api/queries/jobs/useJob';

import { JobData } from './components/JobDetails';
import { CTCJobDto, computeJobData, readDivisionFilters } from './helpers';

export interface GroupedDetails {
  [costCode: string]: JobCostDetailDto[];
}

export interface GroupedSummary {
  [costCode: string]: JobCostCodeSummaryGpDto[];
}

export interface CTCJobView {
  job: JobSummaryDto;
  linkedJob: CTCJobDto | null;
  status: IntegrationStatusDto;
  nextDetails: { next: string; previous: string };
  materials: Record<string, JobPoDataDto[]>;
  materialsTotal: { value: number; remaining: number };
  details: GroupedDetails;
  summary: GroupedSummary;
  summaryList: JobCostCodeSummaryGpDto[];
  adjustments: CostCodeAdjustmentItem[];
  adjustedRev: number;
  bonded: string;
  customerName: string;
  jobData: JobData;
  invoices: JobInvoiceDataDto[];
  changeOrders: JobChangeOrderDto[];
}

/**
 * Composes the CTC job page from independent per-entity queries so a write
 * (e.g. a cost-code adjustment) can refresh only the subtree it changed.
 */
export function useCTCJobView(jobId: string) {
  const divisions = React.useMemo(() => readDivisionFilters(), []);

  const jobQuery = useCtcJob(jobId, ctcSnapshotOptions);
  const statusQuery = useCtcCurrentMonthStatus({
    ...ctcSnapshotOptions,
    enabled: !!jobId,
  });
  const navigationQuery = useCtcNavigation(
    jobId,
    divisions,
    ctcSnapshotOptions,
  );
  const materialsQuery = useCtcMaterials(jobId, ctcSnapshotOptions);
  const materialsTotalQuery = useCtcMaterialsTotal(jobId, ctcSnapshotOptions);
  const adjustmentsQuery = useCtcAdjustments(jobId, ctcSnapshotOptions);
  const linkedJobQuery = useJob(jobQuery.data?.importJobId, {
    gcTime: 0,
    optional: true,
  });

  const parts = [
    jobQuery,
    statusQuery,
    navigationQuery,
    materialsQuery,
    materialsTotalQuery,
    adjustmentsQuery,
  ];

  const error = parts.find((part) => part.error)?.error ?? null;
  const isLoading = parts.some((part) => part.isLoading);
  const isFetching = parts.some((part) => part.isFetching);

  const result = jobQuery.data;
  // Several of these endpoints answer 204 for an empty resource, which the
  // generated client resolves to null, so readiness tracks query state and not
  // the presence of a payload.
  const ready =
    parts.every((part) => part.isSuccess) &&
    !!result &&
    // A linked job that cannot be read is rendered as "not linked".
    (!result.importJobId || !linkedJobQuery.isLoading);

  const data = React.useMemo<CTCJobView | undefined>(() => {
    if (!ready || !result) return undefined;

    const detailsList = result.details ?? [];
    const summaryList = result.summary ?? [];
    const firstDetail = detailsList[0];
    const bonded = firstDetail ? firstDetail.bonded ?? '' : 'N/A';

    // result.job is typed as optional in CtcJobDetailResponse; at runtime it is
    // always present for a valid job.
    const job = result.job as JobSummaryDto;

    return {
      job,
      linkedJob: (linkedJobQuery.data as CTCJobDto | undefined) ?? null,
      status: statusQuery.data as IntegrationStatusDto,
      nextDetails: (navigationQuery.data as {
        next: string;
        previous: string;
      } | null) ?? { next: '', previous: '' },
      materials: materialsQuery.data ?? {},
      materialsTotal: (materialsTotalQuery.data as {
        value: number;
        remaining: number;
      } | null) ?? { value: 0, remaining: 0 },
      details: groupBy(detailsList, 'costCode'),
      summary: groupBy(summaryList, 'costCode'),
      summaryList,
      adjustments: adjustmentsQuery.data?.adjustments ?? [],
      adjustedRev: adjustmentsQuery.data?.adjustedRev ?? 0,
      bonded,
      customerName: job.custName ?? '',
      jobData: computeJobData(job, firstDetail, summaryList),
      invoices: result.invoices ?? [],
      changeOrders: result.changeOrders ?? [],
    };
  }, [
    ready,
    result,
    linkedJobQuery.data,
    statusQuery.data,
    navigationQuery.data,
    materialsQuery.data,
    materialsTotalQuery.data,
    adjustmentsQuery.data,
  ]);

  return {
    data,
    error,
    isError: !!error,
    isLoading,
    isFetching,
    isSuccess: !error && data !== undefined,
  };
}
